import { Autocomplete, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { paramCase } from 'change-case';
import type { FieldArrayRenderProps, FieldProps } from 'formik';
import { Field, FormikProvider, useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DrilldownItem from '@components/DrilldownItem';
import RoundedButton from '@components/RoundedButton/index';

import { ColumnLayout } from '@layouts/DrilldownStyle';

import type { FormValues } from '@models/diagnostic-report';
import type { LabObservationValues } from '@models/lab-result';

import { isBooleanValue, isNumberValue } from '@utils/lab';
import { getLabObservationTypes, getUnitOptions } from '@utils/records';

import { Title } from './SingleLabDrilldown.style';

const messages = defineMessages({
  testName: {
    id: 'labObservationForm.testName',
    defaultMessage: 'Test Name',
  },
  unit: {
    id: 'labObservationForm.unit',
    defaultMessage: 'Unit',
  },
  value: {
    id: 'labObservationForm.value',
    defaultMessage: 'Value',
  },
  removeResult: {
    id: 'labObservationForm.removeResult',
    defaultMessage: 'Remove Result',
  },
});

const LabObservationForm: React.FC<{
  arrayHelpers: FieldArrayRenderProps;
  code?: string | null;
  value: LabObservationValues;
  index: number;
}> = ({ arrayHelpers, code, value, index = 0 }) => {
  const { formatMessage } = useIntl();
  const formik = useFormikContext<FormValues>();
  const { setFieldValue } = formik;
  const { palette } = useTheme();

  const result = value;

  const unitOptions = useMemo(
    () => getUnitOptions(code ?? '', result.code),
    [code, result.code]
  );
  const unitOptionsLength = Object.keys(unitOptions).length;

  const numberValue =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    isNumberValue(result.value?.toString()) &&
    !unitOptions.map(({ v }) => v).includes('Positive');
  const booleanValue =
    isBooleanValue(result.value) ||
    unitOptions.map(({ v }) => v).includes('Positive');

  const handleCodeChange = (
    value:
      | string
      | {
          k: string;
          v: string;
        }
  ) => {
    if (typeof value === 'string') {
      setFieldValue(`results.${index}.code`, paramCase(value));
      setFieldValue(`results.${index}.displayName`, value);
    } else {
      setFieldValue(`results.${index}.code`, value.k);
      setFieldValue(`results.${index}.displayName`, value.v);
    }
  };

  return (
    <FormikProvider value={formik}>
      <ColumnLayout>
        <div>
          <Title color={palette.primary.main} fontSize={15}>
            {formatMessage(messages.testName)}
          </Title>
          <Autocomplete
            disableClearable
            freeSolo
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.v
            }
            onChange={(_event, value) => {
              // Event change triggered by mouse click on option
              handleCodeChange(value);
            }}
            options={getLabObservationTypes(code ?? '')}
            renderInput={(params) => (
              <Field name={`results.${index}.code`}>
                {({ field, meta }: FieldProps<string>) => (
                  <TextField
                    {...params}
                    {...field}
                    error={Boolean(meta.error)}
                    helperText={meta.touched ? meta.error : ''}
                    margin="dense"
                    onChange={(event) => {
                      const { value } = event.target;
                      // Event change triggered by typing
                      handleCodeChange(value);
                    }}
                    required
                  />
                )}
              </Field>
            )}
            renderOption={(
              props: any,
              option: {
                k: string;
                v: string;
              }
            ) => {
              return <li {...props}>{`${option.v}`}</li>;
            }}
            sx={{
              width: 305,
            }}
            value={result.displayName ?? ''}
          />
        </div>
        {index !== 0 && !result.id ? (
          <RoundedButton
            autoSize
            fontSize={13}
            height={40}
            onClick={() => arrayHelpers.remove(index)}
            type="button"
          >
            {formatMessage(messages.removeResult)}
          </RoundedButton>
        ) : null}
      </ColumnLayout>

      <ColumnLayout>
        {booleanValue ? (
          <DrilldownItem
            label={formatMessage(messages.value)}
            name={`results.${index}.value`}
            onChange={(value) => {
              if (value === 'true' || value === 'false') {
                setFieldValue(
                  `results.${index}.value`,
                  Boolean(value === 'true')
                );
              }
              setFieldValue(`results.${index}.value`, value);
            }}
            options={[
              {
                k: 'true',
                v: 'Positive',
              },
              {
                k: 'false',
                v: 'Negative',
              },
            ]}
            type="dropdown"
            value={result.value ? 'Positive' : 'Negative'}
          />
        ) : (
          <DrilldownItem
            label={formatMessage(messages.value)}
            name={`results.${index}.value`}
            type={code === 'labs--pathology' ? 'text-area' : 'text'}
            value={result.value}
          />
        )}

        {/*
         * -- If value is of type string, do not show unit input
         * -- If unit options' length is 0, display as text input
         */}
        {/* eslint-disable-next-line no-nested-ternary */}
        {unitOptionsLength > 0 && (numberValue || !result.value) ? (
          <DrilldownItem
            label={formatMessage(messages.unit)}
            name={`results.${index}.unit`}
            onChange={(value) => setFieldValue(`results.${index}.unit`, value)}
            options={unitOptions}
            type="dropdown"
            value={result.unit}
          />
        ) : (unitOptionsLength === 0 && numberValue) || !result.value ? (
          <DrilldownItem
            label={formatMessage(messages.unit)}
            name={`results.${index}.unit`}
            value={result.unit}
          />
        ) : null}
      </ColumnLayout>
    </FormikProvider>
  );
};

export default LabObservationForm;
